<template>
  <div class="widget-phone-input">
    <label
      v-if="label"
      for="phone-input"
      :data-error="!!error || !!errorMessage"
      :data-subtitle="!!subtitle"
      :data-active="isInputFocused || !!formattedNumber"
      class="widget-phone-input_label"
      :class="componentStyles?.inputLabel || ''"
      @click="() => phoneInput?.focus()"
    >
      {{ label }}
    </label>
    <VueTelInput
      v-show="!hidden"
      v-model="formattedNumber"
      ref="phoneInput"
      mode="international"
      name="phone-input"
      auto-format
      valid-characters-only
      :input-options="{ placeholder, autocomplete: 'tel', inputmode: 'tel' }"
      :preferred-countries="['GB', 'US']"
      :class="[twMerge(String($attrs.class || ''), `${label ? 'mt-1' : ''}`, innerClass), 'phone-input-inner']"
      :data-error="!!error || !!errorMessage"
      @on-input="validatePhone"
      @click.prevent.stop
      @focus="() => (isInputFocused = true)"
      @blur="() => (isInputFocused = false)"
      @update:model-value="formattedNumber = $event.slice(0, formattedNumberLength)"
    />
    <div v-if="error || errorMessage" class="mt-1 grid grid-flow-col justify-start gap-2">
      <WidgetIcon
        v-if="
          !checkTheme(
            'volvo',
            'tesla',
            'aa',
            'suzuki',
            'cinch',
            'crashdaddy',
            'volkswagen',
            'bentley',
            'vwg',
            'audi',
            'samsung'
          )
        "
        :icon="themeIcons.closeCircle"
        height="1.25rem"
        width="1.25rem"
        class="mt-0.5 !text-negative"
      ></WidgetIcon>
      <p class="font-normal text-negative">{{ error || errorMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { VueTelInput } from 'vue-tel-input';
  import { Phone } from 'src/types/overlays';
  import { twMerge } from 'tailwind-merge';
  import { themeIcons, checkTheme, getComponentStyle } from 'src/utils/config';
  import WidgetIcon from './WidgetIcon.vue';

  defineOptions({ inheritAttrs: false });

  export type PhoneObject = {
    country: { name: string; iso2: string; dialCode: string };
    countryCallingCode?: string;
    countryCode?: string;
    formatted?: string;
    nationalNumber?: string;
    number?: string;
    valid?: boolean;
  };

  const props = withDefaults(
    defineProps<{
      phone?: string;
      label?: string;
      placeholder?: string;
      subtitle?: boolean;
      errorMessage?: string;
      innerClass?: string;
      hidden?: boolean;
    }>(),
    {
      subtitle: false,
      phone: '',
      errorMessage: '',
      innerClass: '',
      label: '',
      placeholder: 'Enter a phone number',
      variant: undefined,
      hidden: false,
    }
  );
  const emit = defineEmits<{ (e: 'phoneChanged', phone: Phone): void; (e: 'error', error: boolean): void }>();

  const componentStyles = getComponentStyle();
  const phoneInput = ref<HTMLInputElement>();
  const formattedNumber = ref(props.phone || '');
  const error = ref('');
  const isInputFocused = ref(false);
  const formattedNumberLength = ref(0);

  function validatePhone(_phoneNumber: string, phoneObject: PhoneObject) {
    // nationalNumber + countryCallingCode + 1 for the '+' sign + 3 characters reserved for spaces and brackets
    formattedNumberLength.value = 11 + (phoneObject.countryCallingCode?.length || 0 + 1) + 3;
    emit('phoneChanged', {
      countryCallingCode: phoneObject.countryCallingCode || '',
      nationalNumber: phoneObject.nationalNumber?.slice(0, 11) || '',
      formatted: phoneObject.formatted?.slice(0, formattedNumberLength.value) || '',
    });
    if (phoneObject.valid === false) {
      error.value = 'Invalid phone number';
      emit('error', true);
      return;
    }
    error.value = '';
    emit('error', false);
  }

  watch(
    () => props.phone,
    () => {
      formattedNumber.value = props.phone || '';
    }
  );
</script>

<style scoped></style>
